import type { ServerSetting } from '../../../services/adminServerSettings';
import type { AzureCosts, CostPrices, UnitHours } from '../../../services/adminUnits';
import { UnitsCpuCoresKey, UnitsMemoryGbKey, UnitsWarmCountKey, appliedValue } from './unitsPolicy';

/** Azure's hours in a month. */
export const HoursPerMonth = 730;

export interface EstimatePolicy {
  cores: number;
  memoryGb: number;
  warmUnits: number;
  /** A value in the policy's controls differs from the stored one. */
  unsaved: boolean;
}

export interface CostEstimate {
  unitPerHour: number;
  /** The window's unit-hours, at the policy's size, scaled to a month. */
  usage: number;
  warm: number;
  manager: number;
  total: number;
  /** The usage's cost per job done in the window; null without a job. */
  perJob: number | null;
}

function policyNumber(settings: ServerSetting[], drafts: Record<string, string>, key: string) {
  const applied = Number(appliedValue(settings, key));
  const draft = drafts[key]?.trim();
  const stored = settings.find((setting) => setting.key === key)?.value;
  const edited = draft && draft !== stored ? Number(draft) : NaN;
  const typed = Number.isFinite(edited) && edited >= 0;
  const value = typed ? edited : applied;
  return { value: Number.isFinite(value) ? value : 0, unsaved: typed && edited !== applied };
}

/**
 * The policy the estimate prices: each value as it is typed in its control, the applied one while the control holds
 * the stored value, nothing or no number.
 */
export function estimatePolicy(settings: ServerSetting[], drafts: Record<string, string>): EstimatePolicy {
  const cores = policyNumber(settings, drafts, UnitsCpuCoresKey);
  const memory = policyNumber(settings, drafts, UnitsMemoryGbKey);
  const warm = policyNumber(settings, drafts, UnitsWarmCountKey);
  return {
    cores: cores.value,
    memoryGb: memory.value,
    warmUnits: warm.value,
    unsaved: cores.unsaved || memory.unsaved || warm.unsaved,
  };
}

/** Next month: the window's unit-hours at the policy's size, warm units for every hour, and the manager's app. */
export function estimateNextMonth(hours: UnitHours, prices: CostPrices, policy: EstimatePolicy): CostEstimate {
  const unitPerHour = policy.cores * prices.unitCorePerHour + policy.memoryGb * prices.unitGbPerHour;
  const windowCost = hours.hours * unitPerHour;
  const usage = windowCost * (HoursPerMonth / (hours.days * 24));
  const warm = policy.warmUnits * HoursPerMonth * unitPerHour;
  const manager = prices.managerAppPerMonth;
  return {
    unitPerHour,
    usage,
    warm,
    manager,
    total: usage + warm + manager,
    perJob: hours.jobsDone > 0 ? windowCost / hours.jobsDone : null,
  };
}

/** An amount in its currency, such as "$206.28"; small amounts keep enough digits to say something. */
export function formatMoney(amount: number, currency: string, fractionDigits = 2): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(amount);
  } catch {
    return `${amount.toFixed(fractionDigits)} ${currency}`.trim();
  }
}

const utcDay = (day: string) => new Date(`${day}T00:00:00Z`);

/** A period as its column heading: "Sep 2026" for a whole month, "Oct 1–19" for the month so far. */
export function periodLabel(costs: AzureCosts, wholeMonth: boolean): string {
  const first = utcDay(costs.firstDay);
  if (wholeMonth) return first.toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
  const month = first.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' });
  return `${month} ${first.getUTCDate()}–${utcDay(costs.lastDay).getUTCDate()}`;
}
